import image from "../../Images/ContainerWebApp.png"
import "../ContainerWebApp/ContainerWebApp.css"
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneDark} from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactMarkdown from "react-markdown";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const ContainerWebApp = () => {
    const [activeSection, setActiveSection] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
            const handleScroll = () => {
                const sections = document.querySelectorAll("[data-section]");
                let current = "";
    
                sections.forEach((section) => {
                    const sectionTop = section.offsetTop;
                    const sectionHeight = section.clientHeight;
                    if (window.pageYOffset >= sectionTop - 100) {
                        current = section.getAttribute("data-section");
                    }
                });
    
                setActiveSection(current);
            };
    
            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
        }, []);
    
        const scrollToSection = (sectionId) => {
            const element = document.querySelector(`[data-section="${sectionId}"]`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        };

    return (
        <div className="documentation-layout">
            <aside className="sidebar">
                <nav className="sidebar-nav">
                    <button className="back-button" onClick={() => navigate('/')}>
                        ← Back
                    </button>
                    <h3 className="sidebar-title">Contents</h3>
                    <ul className="sidebar-list">

                        <li>
                            <a 
                                className={activeSection === "architecture" ? "active" : ""}
                                onClick={() => scrollToSection("architecture")}
                            >
                                Architecture
                            </a>
                        </li>
                            <a 
                                className={activeSection === "details" ? "active" : ""}
                                onClick={() => scrollToSection("details")}
                            >
                                Details
                            </a>
                        <li>
                            <a 
                                className={activeSection === "instantiation-code" ? "active" : ""}
                                onClick={() => scrollToSection("instantiation-code")}
                            >
                                Instantiation
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "stack-code" ? "active" : ""}
                                onClick={() => scrollToSection("stack-code")}
                            >
                                Stack Code
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "implementation-details" ? "active" : ""}
                                onClick={() => scrollToSection("implementation-details")}
                            >
                                Implementation Details
                            </a>
                        </li>
                    </ul>
                </nav>
            </aside>
            <main className="documentation-page">
                <div className="title-container" data-section="title">
                    <ReactMarkdown>
                        {`# Container Web App Stack`}
                    </ReactMarkdown>
                </div>
                <div className="image-container">
                    <img className="image" src={image} />
                </div>
                <div className="markdown-left" data-section="details">
                    <ReactMarkdown>
                        {`
## Architecture
- **VPC**: Virtual Private Cloud within AWS region, spanning multiple availability zones for high availability
- **Availability Zone**: Isolated locations within a region, each containing public and private subnets
- **Public Subnet**: Contains Application Load Balancers accessible from the internet
- **Private Subnet (ECS)**: Hosts Fargate containers running application
- **Private Subnet (RDS**: Hosts RDS database instances
- **Application Load Balancer (ALB)**: Distributes traffic across Fargate tasks in multiple AZs
- **ECS Fargate**: Serverless container compute running application tasks
- **RDS**: Managed relational database service  

## Use Cases
- Web Applications requiring high availability
- Containerized microservices
- Workloads needing load balancing and auto-scaling
- Applications requiring private networks between compute and data layers

## Cost
- **Application Load Balancer**: ~$16-25/month per ALB
- **NAT Gateway**: ~$32/month per NAT Gateway
- **RDS**: ~$15-500+/month, varying widely by instance size and engine
- **ECS Fargate**: ~$0.12/hour per task (1 vCPU, 2GB RAM) - only runs when tasks are active
  - Light usage (40 hours/month): ~$5/month per task
  - Medium usage (200 hours/month): ~$24/month per task
  - Always-on (730 hours/month): ~$87/month per task
                        `}
                    </ReactMarkdown>
                </div>
<div className="code-title-container" data-section="instantiation-code">
                    <ReactMarkdown>
                        {`# app.ts`}
                    </ReactMarkdown>
                </div>

                    <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
#!/usr/bin/env node
import * as cdk from 'aws-cdk-lib/core';
import { ContainerWebAppStack } from '../lib/container_web_app_stack';

const app = new cdk.App();

new ContainerWebAppStack(app, "DevContainerApp", {
  environment: "dev",
  containerImage: "nginx:latest",
  containerPort: 80,
  desiredCount: 1,
  env: {
    account: process.env.CDK_DEFAULT_ACCOUNT,
    region: process.env.CDK_DEFAULT_REGION
  },
  databaseEngine: "postgres"
})
`}
                    </SyntaxHighlighter>
                </div>
                    
                <div className="code-title-container" data-section="stack-code">
                    <ReactMarkdown>
                        {`# container-webapp-stack.ts`}
                    </ReactMarkdown>
                </div>

                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
                        {`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as ec2 from "aws-cdk-lib/aws-ec2";
import * as ecs from "aws-cdk-lib/aws-ecs";
import * as logs from "aws-cdk-lib/aws-logs";
import * as elbv2 from "aws-cdk-lib/aws-elasticloadbalancingv2";
import * as rds from "aws-cdk-lib/aws-rds";

interface ContainerWebAppStackProps extends cdk.StackProps {
  environment: "dev" | "prod";
  containerImage: string;
  containerPort?: number;
  desiredCount?: number;
  cpu?: number;
  memory?: number;
  healthCheckPath?: string;
  databaseEngine?: "postgres" | 'mysql';
}

export class ContainerWebAppStack extends cdk.Stack {
  constructor(scope: Construct, id: string, props: ContainerWebAppStackProps) {
    super(scope, id, props)
  

    const environment = props.environment || "dev";

    const vpc = new ec2.Vpc(this, "AppVpc", {
      maxAzs: environment === "prod" ? 3 : 2,
      natGateways: environment === "prod" ? 3 : 2,
    })

    const cluster = new ecs.Cluster(this, "AppCluster", {
      vpc: vpc,
      containerInsights: environment === "prod",
    })

    const taskDefinition = new ecs.FargateTaskDefinition(this, "AppTaskDef", {
      cpu: props.cpu || (environment === "prod" ? 512 : 256),
      memoryLimitMiB: props.memory || (environment === "prod" ? 1024 : 512),
    })


    const alb = new elbv2.ApplicationLoadBalancer(this, "AppLoadBalancer", {
      vpc: vpc,
      internetFacing: true,
    })

    const listener = alb.addListener("HttpListener", {
      port: 80,
      protocol: elbv2.ApplicationProtocol.HTTP,
    })

    const service = new ecs.FargateService(this, "AppService", {
      cluster: cluster,
      taskDefinition: taskDefinition,
      desiredCount: props.desiredCount || (environment === 'prod' ? 2 : 1),
      assignPublicIp: false,
      healthCheckGracePeriod: cdk.Duration.seconds(60),

    })

   

    const database = new rds.DatabaseInstance(this, "AppDatabase", {
      engine: props.databaseEngine === "postgres" ? 
      rds.DatabaseInstanceEngine.postgres({
        version: rds.PostgresEngineVersion.VER_15,
      }) :
      rds.DatabaseInstanceEngine.mysql({
        version: rds.MysqlEngineVersion.VER_8_0,
      }),
      vpc: vpc,
      vpcSubnets: {
        subnetType: ec2.SubnetType.PRIVATE_WITH_EGRESS,

      },
      instanceType: ec2.InstanceType.of(
        ec2.InstanceClass.T3,
        environment === "prod" ? ec2.InstanceSize.SMALL : ec2.InstanceSize.MICRO
      ),
      allocatedStorage: environment === "prod" ? 100 : 20,
      maxAllocatedStorage: environment == "prod" ? 200 : 50,
      removalPolicy: cdk.RemovalPolicy.DESTROY,
      deletionProtection: false,
    })

    database.connections.allowFrom(service, ec2.Port.tcp(5432), "Allow from Fargate");

    const container = taskDefinition.addContainer("AppContainer", {
      image: ecs.ContainerImage.fromRegistry(props.containerImage),
      logging: ecs.LogDrivers.awsLogs({
        streamPrefix: "app",
        logRetention: logs.RetentionDays.ONE_WEEK,
      }),
      environment: {
        ENVIRONMENT: environment,
        DB_HOST: database.dbInstanceEndpointAddress,
        DB_PORT: database.dbInstanceEndpointPort,
        DBNAME: "myapp"
      },
      secrets: {
        DB_PASSWORD: ecs.Secret.fromSecretsManager(database.secret!, 'password'),
      }
    })

    container.addPortMappings({
      containerPort:props.containerPort || 80,
      protocol: ecs.Protocol.TCP,
    })

    listener.addTargets("AppTargets", {
      port: 80,
      targets: [service],
      healthCheck: {
        path: props.healthCheckPath || '/',
        interval: cdk.Duration.seconds(30),
        timeout: cdk.Duration.seconds(5),
        healthyThresholdCount: 2,
        unhealthyThresholdCount: 3,
      }
    })

    new cdk.CfnOutput(this, "LoadBalancerUrl", {
      value: ${"http://${alb.loadBalancerDnsName}"},
      description: "Application URL",
    })

    new cdk.CfnOutput(this, "ClusterName", {
      value: cluster.clusterName,
      description: "ECS Cluster name",
    })

    new cdk.CfnOutput(this, "ServiceName", {
      value: service.serviceName,
      description: "ECS Service Name"
    })

  }

}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="code-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Implementation Details`}
                    </ReactMarkdown>
                </div>
<div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Import Statements & Stack Props`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as ec2 from "aws-cdk-lib/aws-ec2";
import * as ecs from "aws-cdk-lib/aws-ecs";
import * as logs from "aws-cdk-lib/aws-logs";
import * as elbv2 from "aws-cdk-lib/aws-elasticloadbalancingv2";
import * as rds from "aws-cdk-lib/aws-rds";

interface ContainerWebAppStackProps extends cdk.StackProps {
  environment: "dev" | "prod";
  containerImage: string;
  containerPort?: number;
  desiredCount?: number;
  cpu?: number;
  memory?: number;
  healthCheckPath?: string;
  databaseEngine?: "postgres" | 'mysql';
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Imports
- **aws-cdk-lib/core** - Core CDK constructs for defining stacks, apps, and basic infrastructure components
- **constructs** - Base Construct class that all CDK constructs extend from
- **aws-ec2** - VPC and networking constructs for creating virtual private clouds, subnets, and security groups
- **aws-ecs** - Elastic Container Service constructs for running Docker containers in a managed cluster
- **aws-logs** - CloudWatch Logs constructs for centralized logging and log retention management
- **aws-elasticloadbalancingv2** - Application Load Balancer constructs for distributing traffic across containers
- **aws-rds** - Relational Database Service constructs for managed database instances
## Props
- **environment** -  deployment environment identifier ("dev" or "prod") for environment-specific configurations
- **containerImage** -  Docker image URI or name to deploy (e.g., "nginx:latest" or ECR repository URI)
- **containerPort** -  port the container listens on, defaults to common ports if not specified (e.g., 80, 3000)
- **desiredCount** -  number of container tasks to run, affects availability and cost
- **cpu** -  CPU units allocated to each container task (256 = 0.25 vCPU, 512 = 0.5 vCPU, etc.)
- **memory** -  memory in MB allocated to each container task
- **healthCheckPath** -  HTTP path for load balancer health checks (e.g., "/health", "/api/status")
- **databaseEngine** -  database type to provision, either PostgreSQL or MySQL

`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Constructor & VPC`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
export class ContainerWebAppStack extends cdk.Stack {
  constructor(scope: Construct, id: string, props: ContainerWebAppStackProps) {
    super(scope, id, props)
  

    const environment = props.environment || "dev";

    const vpc = new ec2.Vpc(this, "AppVpc", {
      maxAzs: environment === "prod" ? 3 : 2,
      natGateways: environment === "prod" ? 3 : 2,
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Constructor
- Takes a scope (parent construct), id (unique identifier), and optional props to configure the stack
- Calls super() to initialize the parent cdk.Stack class with the provided parameters
## VPC
- Creates a Virtual Private Cloud for isolating the application's networking resources
- **maxAzs** - Distributes resources across 3 Availability Zones in production for high availability, 2 AZs in development to reduce costs
- **natGateways** - Provisions 3 NAT Gateways in production (one per AZ for redundancy), 2 in development to balance cost and availability
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# ECS (Elastic Container Service)`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   const cluster = new ecs.Cluster(this, "AppCluster", {
      vpc: vpc,
      containerInsights: environment === "prod",
    })

    const taskDefinition = new ecs.FargateTaskDefinition(this, "AppTaskDef", {
      cpu: props.cpu || (environment === "prod" ? 512 : 256),
      memoryLimitMiB: props.memory || (environment === "prod" ? 1024 : 512),
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Cluster
- Creates an ECS cluster to manage and orchestrate container deployments within the VPC
- **vpc** - Associates the cluster with the previously created VPC for networking
- **containerInsights** - Enables CloudWatch Container Insights monitoring in production for detailed metrics and logs, disabled in development to reduce costs
## Task
- Defines the configuration blueprint for running containers using AWS Fargate (serverless compute for containers)
- **cpu** - Allocates 512 CPU units (0.5 vCPU) in production or 256 units (0.25 vCPU) in development, unless custom value provided in props
- **memoryLimitMiB** - Allocates 1024 MB (1 GB) memory in production or 512 MB in development, unless custom value provided in props
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# ALB (Application Load Balancer)`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   const alb = new elbv2.ApplicationLoadBalancer(this, "AppLoadBalancer", {
      vpc: vpc,
      internetFacing: true,
    })

    const listener = alb.addListener("HttpListener", {
      port: 80,
      protocol: elbv2.ApplicationProtocol.HTTP,
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## ALB
- Creates an ALB to distribute incoming traffic across multiple container instances
- **vpc** - Deploys the load balancer within the previously created VPC
- **internetFacing: true** - Places the ALB in public subnets with a public IP, allowing it to receive traffic from the internet
## Listener
- Configures the load balancer to listen for incoming requests on port 80
- **port: 80** - Standard HTTP port for web traffic
- **protocol: HTTP** - Uses unencrypted HTTP protocol (for production, consider adding HTTPS on port 443 with SSL/TLS certificates)
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Fargate`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   const service = new ecs.FargateService(this, "AppService", {
      cluster: cluster,
      taskDefinition: taskDefinition,
      desiredCount: props.desiredCount || (environment === 'prod' ? 2 : 1),
      assignPublicIp: false,
      healthCheckGracePeriod: cdk.Duration.seconds(60),

    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Fargate
- Creates an ECS service that runs and maintains the specified number of container tasks based on the task definition
- **cluster** - Deploys the service within the previously created ECS cluster
- **taskDefinition** - Uses the task definition blueprint to launch container instances
- **desiredCount** - Maintains 2 running tasks in production for high availability, 1 task in development to minimize costs (unless overridden in props)
- **assignPublicIp: false** - Places tasks in private subnets without public IPs for better security, they access the internet through NAT Gateways
- **healthCheckGracePeriod** - Waits 60 seconds after task startup before evaluating health checks, allowing containers time to initialize
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# RDS Database`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   const database = new rds.DatabaseInstance(this, "AppDatabase", {
      engine: props.databaseEngine === "postgres" ? 
      rds.DatabaseInstanceEngine.postgres({
        version: rds.PostgresEngineVersion.VER_15,
      }) :
      rds.DatabaseInstanceEngine.mysql({
        version: rds.MysqlEngineVersion.VER_8_0,
      }),
      vpc: vpc,
      vpcSubnets: {
        subnetType: ec2.SubnetType.PRIVATE_WITH_EGRESS,

      },
      instanceType: ec2.InstanceType.of(
        ec2.InstanceClass.T3,
        environment === "prod" ? ec2.InstanceSize.SMALL : ec2.InstanceSize.MICRO
      ),
      allocatedStorage: environment === "prod" ? 100 : 20,
      maxAllocatedStorage: environment == "prod" ? 200 : 50,
      removalPolicy: cdk.RemovalPolicy.DESTROY,
      deletionProtection: false,
    })

    database.connections.allowFrom(service, ec2.Port.tcp(5432), "Allow from Fargate");
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## RDS Setup
- Conditionally creates either a PostgreSQL 15 or MySQL 8.0 database based on the databaseEngine prop
- **vpc** - Deploys the database within the same VPC as the application
- **vpcSubnets** - Places the database in private subnets with egress (internet access via NAT) for security
- **instanceType** - Uses t3.small in production for better performance, t3.micro in development for cost savings
- **allocatedStorage** - Provisions 100 GB storage in production, 20 GB in development
- **maxAllocatedStorage** - Allows automatic storage scaling up to 200 GB in production, 50 GB in development
- **removalPolicy: DESTROY** - Automatically deletes the database when the stack is destroyed (useful for dev/testing)
- **deletionProtection: false** - Allows database deletion without additional safeguards (should be true in production)
## Network Access
- **allowFrom(service, Port.tcp(5432))** - Grants the Fargate service access to the database on port 5432 (PostgreSQL default; should be 3306 for MySQL)
- Automatically configures security group rules to allow connectivity between containers and database
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Container Image`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   const container = taskDefinition.addContainer("AppContainer", {
      image: ecs.ContainerImage.fromRegistry(props.containerImage),
      logging: ecs.LogDrivers.awsLogs({
        streamPrefix: "app",
        logRetention: logs.RetentionDays.ONE_WEEK,
      }),
      environment: {
        ENVIRONMENT: environment,
        DB_HOST: database.dbInstanceEndpointAddress,
        DB_PORT: database.dbInstanceEndpointPort,
        DBNAME: "myapp"
      },
      secrets: {
        DB_PASSWORD: ecs.Secret.fromSecretsManager(database.secret!, 'password'),
      }
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Container Setup
- Adds a container to the task definition using the Docker image specified in props
- **image** - Pulls the container image from a registry (Docker Hub, Amazon ECR, or other public/private registries)
- **logging** - Sends container logs to CloudWatch Logs for centralized monitoring and debugging
- **streamPrefix: "app"** - Prefixes log stream names with "app" for easier identification
- **logRetention: ONE_WEEK** - Automatically deletes logs older than 7 days to control storage costs
- **ENVIRONMENT** - Passes the deployment environment ("dev" or "prod") to the application 
- **DB_HOST** - Provides the database endpoint address for establishing connections
- **DB_PORT** - Provides the database port number (5432 for PostgreSQL, 3306 for MySQL)
- **DBNAME** - Sets the database name to "myapp"
- **DB_PASSWORD** - Securely injects the database password from AWS Secrets Manager as an environment variable 
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Networking Configurations`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    container.addPortMappings({
      containerPort:props.containerPort || 80,
      protocol: ecs.Protocol.TCP,
    })

    listener.addTargets("AppTargets", {
      port: 80,
      targets: [service],
      healthCheck: {
        path: props.healthCheckPath || '/',
        interval: cdk.Duration.seconds(30),
        timeout: cdk.Duration.seconds(5),
        healthyThresholdCount: 2,
        unhealthyThresholdCount: 3,
      }
    })
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Container port mappings
- Maps the container's internal port to allow external traffic to reach the application
- **containerPort** - Exposes port 80 by default, or uses custom port from props (should match the port your application listens on)
- **protocol: TCP** - Uses TCP protocol for standard HTTP/HTTPS traffic
## ALB Target Group
- Registers the Fargate service as a target for the ALB listener to route traffic to
- **port: 80** - Load balancer forwards traffic to containers on port 80
- **targets: [service]** - Directs traffic to all tasks running in the Fargate service
## Health Check Configuration
- **path** - Health check endpoint, defaults to "/" or uses custom path from props (e.g., "/health", "/api/status")
- **interval** - Performs health checks every 30 seconds to monitor task availability
- **timeout** - Waits up to 5 seconds for a response before considering the check failed
- **healthyThresholdCount: 2** - Requires 2 consecutive successful checks before marking a task as healthy
- **unhealthyThresholdCount: 3** - Requires 3 consecutive failed checks before marking a task as unhealthy and replacing it
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Outputs`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    new cdk.CfnOutput(this, "LoadBalancerUrl", {
      value: ${"http://${alb.loadBalancerDnsName}"},
      description: "Application URL",
    })

    new cdk.CfnOutput(this, "ClusterName", {
      value: cluster.clusterName,
      description: "ECS Cluster name",
    })

    new cdk.CfnOutput(this, "ServiceName", {
      value: service.serviceName,
      description: "ECS Service Name"
    })

  
  }
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Outputs
- These outputs display important values in the terminal after deployment and can be referenced by other stacks or automation tools
`}
                    </ReactMarkdown>
                    </div>
            </main>
        </div>
    )

}
export default ContainerWebApp;